import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Printer } from "lucide-react";

export default function Diplomas() {
  const { user } = useAuth();
  const [plantelId, setPlantelId] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState("");
  const [alumnos, setAlumnos] = useState([]);
  const [alumnoId, setAlumnoId] = useState("");
  const [lugar, setLugar] = useState("1");
  const [periodo, setPeriodo] = useState("1");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [ciudad, setCiudad] = useState("Cd. y Pto. de Tampico, Tam.");

  useEffect(() => { (async () => {
    try {
      if (user?.role === "docente") {
        const { data } = await api.get("/grupos");
        setGrupos(data);
        if (data.length) { setPlantelId(data[0].plantel_id); setGrupoId(data[0].id); }
      } else {
        const pls = (await api.get("/planteles")).data;
        if (pls.length) {
          setPlantelId(pls[0].id);
          const gs = (await api.get("/grupos", { params: { plantel_id: pls[0].id } })).data;
          setGrupos(gs);
          if (gs.length) setGrupoId(gs[0].id);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  })(); }, [user]);

  useEffect(() => { if (!grupoId) { setAlumnos([]); return; } (async () => {
    try {
      const { data } = await api.get("/alumnos", { params: { grupo_id: grupoId } });
      setAlumnos(data);
      const g = grupos.find((x) => x.id === grupoId);
      if (g?.plantel_id) setPlantelId(g.plantel_id);
    } catch { /* ignore */ }
  })(); }, [grupoId, grupos]);

  const url = useMemo(() => {
    const p = new URLSearchParams();
    p.set("plantel_id", plantelId);
    p.set("grupo_id", grupoId);
    p.set("alumno_id", alumnoId);
    p.set("lugar", lugar);
    p.set("periodo", periodo);
    p.set("fecha", fecha);
    p.set("ciudad", ciudad);
    return `/diplomas/print?${p.toString()}`;
  }, [plantelId, grupoId, alumnoId, lugar, periodo, fecha, ciudad]);

  const canGen = plantelId && grupoId && alumnoId;

  return (
    <AppLayout>
      <div className="max-w-2xl">
        <div className="mb-4">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Documentos imprimibles</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Diplomas</h1>
          <p className="text-sm text-[#545454] mt-1">Diploma tamaño carta horizontal para reconocer 1°, 2° o 3° lugar por periodo.</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded p-5 space-y-3">
          <Field label="Grupo">
            <Select value={grupoId} onValueChange={setGrupoId}>
              <SelectTrigger data-testid="dip-grupo"><SelectValue placeholder="Selecciona grupo…" /></SelectTrigger>
              <SelectContent>{grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Alumno">
            <Select value={alumnoId} onValueChange={setAlumnoId}>
              <SelectTrigger data-testid="dip-alumno"><SelectValue placeholder="Selecciona alumno…" /></SelectTrigger>
              <SelectContent>{alumnos.map((a) => <SelectItem key={a.id} value={a.id}>{[a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" ")}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lugar obtenido">
              <Select value={lugar} onValueChange={setLugar}>
                <SelectTrigger data-testid="dip-lugar"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1° Lugar</SelectItem>
                  <SelectItem value="2">2° Lugar</SelectItem>
                  <SelectItem value="3">3° Lugar</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Periodo">
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger data-testid="dip-periodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Primer periodo</SelectItem>
                  <SelectItem value="2">Segundo periodo</SelectItem>
                  <SelectItem value="3">Tercer periodo</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha"><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="dip-fecha" /></Field>
            <Field label="Ciudad / Lugar"><Input value={ciudad} onChange={(e) => setCiudad(e.target.value)} data-testid="dip-ciudad" /></Field>
          </div>
          <div className="pt-2 flex justify-end border-t border-[#F3F4F6]">
            <Button disabled={!canGen} onClick={() => window.open(url, "_blank")} data-testid="btn-generar-dip" className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Trophy className="w-4 h-4 mr-1.5" /> Generar diploma
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, children }) {
  return (<div><label className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1 block">{label}</label>{children}</div>);
}
